// INSIDO: space feed API service (module 09b-feed-web).
// Stateless wrappers around the additive `/org/*` feed routes (module 09a).
// `stoat.js` only types upstream routes and its request helper drops the
// query/body for unknown routes, so these calls use `fetch` directly.
import type { Client } from "stoat.js";

import { CONFIGURATION } from "@revolt/common";

/**
 * A post in a space feed, as returned by the `/org/*` feed routes
 */
export interface FeedPost {
  /** Post id (ULID) */
  _id: string;
  /** Server (space) id the post belongs to */
  server: string;
  /** Author user id; resolve with `client.users.get(author)` */
  author: string;
  /** Plain-text body */
  content: string;
  /** Post kind */
  kind: "post" | "short";
  /** Attachment (autumn file) ids */
  attachments: string[];
  /** Number of likes */
  likes: number;
  /** Whether the current user liked this post */
  liked: boolean;
  /** Number of comments */
  comments: number;
  /** Creation timestamp */
  created_at: string;
  // INSIDO (09g-reacoes-web): emoji reactions (backend module 09f)
  /** Reaction counts by emoji (only counts > 0) */
  reactions: Record<string, number>;
  /** The current user's reaction, if any */
  myReaction?: string;
}

/**
 * INSIDO (09g-reacoes-web): the emojis a post accepts as reactions, in picker
 * order (heart is U+2764 U+FE0F). One reaction per user per post.
 */
export const REACTION_EMOJIS = ["❤️", "👍", "🎉", "😂", "👏", "🔥"] as const;

/** INSIDO (09g-reacoes-web): one of the allowed reaction emojis */
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

/**
 * INSIDO: a post as the 09a API actually serialises it (`v0::Post`). The UI
 * works with `FeedPost`; `fromWire` maps between the two.
 */
interface WirePost {
  id: string;
  server_id: string;
  author: string;
  body: string;
  kind: "Text" | "Media" | "Short";
  media?: string[];
  like_count: number;
  liked: boolean;
  comment_count: number;
  created_at: string;
  // INSIDO (09g-reacoes-web): optional, absent on backends without 09f
  reactions?: Record<string, number>;
  my_reaction?: string;
}

/**
 * Map a 09a API post to the client `FeedPost` shape
 * @param post Post as returned by the API
 */
function fromWire(post: WirePost): FeedPost {
  return {
    _id: post.id,
    server: post.server_id,
    author: post.author,
    content: post.body,
    kind: post.kind === "Short" ? "short" : "post",
    attachments: post.media ?? [],
    likes: post.like_count,
    liked: post.liked,
    comments: post.comment_count,
    created_at: post.created_at,
    // INSIDO (09g-reacoes-web): keep only positive counts
    reactions: Object.fromEntries(
      Object.entries(post.reactions ?? {}).filter(([, count]) => count > 0),
    ),
    myReaction: post.my_reaction || undefined,
  };
}

/**
 * Body for creating a new post
 */
export interface DataCreatePost {
  content: string;
  attachments?: string[];
  // INSIDO (11-shorts): set to "short" to publish a vertical video notice
  kind?: "short";
}

/**
 * Pagination options for listing posts
 */
export interface ListPostsOptions {
  /** Only return posts older than this post id */
  before?: string;
  /** Maximum number of posts to return */
  limit: number;
}

/** How many times a 429 (ratelimited) `/org/*` call is retried */
const MAX_RATELIMIT_RETRIES = 2;

/**
 * Call an `/org/*` route with the client's authentication header
 * @param client Client
 * @param method HTTP method
 * @param path Route path, starting with `/org/`
 * @param options Query string parameters and JSON body
 */
async function orgRequest<T>(
  client: Client,
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  options: { query?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const search = options.query
    ? new URLSearchParams(options.query).toString()
    : "";
  const url = `${CONFIGURATION.DEFAULT_API_URL}${path}${search ? `?${search}` : ""}`;

  const [authHeader, authHeaderValue] = client.authenticationHeader;
  const headers: Record<string, string> = { [authHeader]: authHeaderValue };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // INSIDO: /org/* shares the API's per-user "any" ratelimit bucket with the
  // client's own startup calls, so a burst (e.g. a few reloads) can answer 429.
  // Honour `retry_after` a couple of times instead of surfacing it as an error.
  let res: Response;
  let data: unknown;
  for (let attempt = 0; ; attempt++) {
    res = await fetch(url, {
      method,
      headers,
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
    data = await res.json().catch(() => null);

    if (res.status !== 429 || attempt >= MAX_RATELIMIT_RETRIES) break;
    const retryAfter = (data as { retry_after?: number } | null)?.retry_after;
    await new Promise((resolve) =>
      setTimeout(resolve, Math.min(retryAfter ?? 1000, 10_000) + 100),
    );
  }

  if (!res.ok) {
    // Always surface the HTTP status so callers can tell 403/404 (forbidden)
    // apart from other failures; keep the parsed body for details.
    throw Object.assign(
      new Error(`${method} ${path} failed with ${res.status}`),
      { status: res.status, data },
    );
  }

  return data as T;
}

/**
 * List posts in a space feed, newest first
 * @param client Client
 * @param serverId Server (space) id
 * @param options Cursor (`before`) and page size (`limit`)
 */
export async function listPosts(
  client: Client,
  serverId: string,
  options: ListPostsOptions,
): Promise<FeedPost[]> {
  const query: Record<string, string> = { limit: String(options.limit) };
  if (options.before) query.before = options.before;

  const posts = await orgRequest<WirePost[]>(
    client,
    "GET",
    `/org/servers/${serverId}/posts`,
    { query },
  );
  return posts.map(fromWire);
}

/**
 * Publish a new post to a space feed
 * @param client Client
 * @param serverId Server (space) id
 * @param data Post content and optional attachment ids
 */
export async function createPost(
  client: Client,
  serverId: string,
  data: DataCreatePost,
): Promise<FeedPost> {
  // INSIDO: the 09a API (`v0::DataCreatePost`) names the fields body/media
  const post = await orgRequest<WirePost>(
    client,
    "POST",
    `/org/servers/${serverId}/posts`,
    {
      body: {
        ...(data.attachments?.length
          ? { body: data.content, media: data.attachments }
          : { body: data.content }),
        // INSIDO (11-shorts): the API's `PostKind` is serialised as "Short"
        ...(data.kind === "short" ? { kind: "Short" } : {}),
      },
    },
  );
  return fromWire(post);
}

/**
 * Like a post
 * @param client Client
 * @param postId Post id
 */
export async function likePost(
  client: Client,
  postId: string,
): Promise<FeedPost> {
  return fromWire(
    await orgRequest<WirePost>(client, "PUT", `/org/posts/${postId}/like`),
  );
}

/**
 * Remove a like from a post
 * @param client Client
 * @param postId Post id
 */
export async function unlikePost(
  client: Client,
  postId: string,
): Promise<FeedPost> {
  return fromWire(
    await orgRequest<WirePost>(client, "DELETE", `/org/posts/${postId}/like`),
  );
}

/**
 * INSIDO (09g-reacoes-web): react to a post (replaces any previous reaction)
 * @param client Client
 * @param postId Post id
 * @param emoji One of `REACTION_EMOJIS`
 */
export async function setReaction(
  client: Client,
  postId: string,
  emoji: string,
): Promise<FeedPost> {
  return fromWire(
    await orgRequest<WirePost>(client, "PUT", `/org/posts/${postId}/reaction`, {
      body: { emoji },
    }),
  );
}

/**
 * INSIDO (09g-reacoes-web): remove the current user's reaction from a post
 * @param client Client
 * @param postId Post id
 */
export async function removeReaction(
  client: Client,
  postId: string,
): Promise<FeedPost> {
  return fromWire(
    await orgRequest<WirePost>(
      client,
      "DELETE",
      `/org/posts/${postId}/reaction`,
    ),
  );
}

/**
 * INSIDO (19-visualizador): what the client knows about an autumn file
 */
export interface FileMeta {
  /** Autumn file id */
  id: string;
  /** Original file name (from autumn's redirect), if known */
  filename?: string;
  /** MIME type of the served file */
  content_type?: string;
  /** Size in bytes of the served file, if reported */
  size?: number;
  /** Inline/preview URL (`/attachments/<id>`) */
  url: string;
  /** Original file URL (`/attachments/<id>/original`) */
  originalUrl: string;
}

/** Cache of file metadata lookups, keyed by file id */
const fileMetaCache = new Map<string, Promise<FileMeta>>();

/**
 * Look up an autumn `attachments` file (feed post media).
 *
 * Autumn has no JSON metadata route. `GET /attachments/<id>/original`
 * redirects to `/attachments/<id>/<filename>`, which is served with the real
 * content type and length; a HEAD request (axum answers HEAD on GET routes and
 * autumn allows CORS) therefore yields the file name (from the redirect), the
 * content type and the size without downloading the file.
 * @param id Autumn file id
 * @param mediaUrl Autumn base URL (defaults to the configured media URL)
 */
export function getFileMeta(
  id: string,
  mediaUrl: string = CONFIGURATION.DEFAULT_MEDIA_URL,
): Promise<FileMeta> {
  const cached = fileMetaCache.get(id);
  if (cached) return cached;

  const base = `${mediaUrl}/attachments/${id}`;
  const lookup = fetch(`${base}/original`, { method: "HEAD" }).then((res) => {
    if (!res.ok) throw new Error(`HEAD ${base} failed with ${res.status}`);
    const finalPath = new URL(res.url).pathname.split("/");
    // [..., "attachments", "<id>", "<filename>"]
    const redirected =
      finalPath[finalPath.length - 2] === id &&
      finalPath[finalPath.length - 1] !== "original";
    const length = res.headers.get("content-length");
    return {
      id,
      filename: redirected
        ? decodeURIComponent(finalPath[finalPath.length - 1])
        : undefined,
      content_type:
        res.headers.get("content-type")?.split(";")[0].trim() || undefined,
      size: length ? Number(length) || undefined : undefined,
      url: base,
      originalUrl: `${base}/original`,
    } satisfies FileMeta;
  });

  // Do not cache failures (e.g. a file not yet claimed by the backend)
  lookup.catch(() => fileMetaCache.delete(id));
  fileMetaCache.set(id, lookup);
  return lookup;
}

// INSIDO (09e-comentarios-web): comments on feed posts (backend module 09d).

/**
 * INSIDO (09e-comentarios-web): a comment on a feed post, as the 09d API
 * serialises it (`v0::Comment`)
 */
export interface FeedComment {
  /** Comment id (ULID); also the pagination cursor */
  id: string;
  /** Post the comment belongs to */
  post_id: string;
  /** Author user id; resolve with `client.users.get(author)` */
  author: string;
  /** Plain-text body (1..2000 characters) */
  body: string;
  /** Creation timestamp */
  created_at: string;
  /** Edit timestamp (reserved; there is no edit route yet) */
  edited_at?: string;
}

/** INSIDO (09e-comentarios-web): default (and UI) comment page size */
export const COMMENTS_PAGE_SIZE = 20;

/** INSIDO (09e-comentarios-web): maximum comment length accepted by the API */
export const COMMENT_MAX_LENGTH = 2000;

/**
 * INSIDO (09e-comentarios-web): pagination options for listing comments
 */
export interface ListCommentsOptions {
  /** Only return comments older than this comment id */
  before?: string;
  /** Maximum number of comments to return (API default 20, max 100) */
  limit?: number;
}

/**
 * INSIDO (09e-comentarios-web): list a page of a post's comments, oldest
 * first. Without `before` it returns the most recent `limit` comments; pass
 * the id of the first comment of the current page to fetch older ones. An
 * empty page means there is nothing older.
 * @param client Client
 * @param postId Post id
 * @param options Cursor (`before`) and page size (`limit`)
 */
export async function listComments(
  client: Client,
  postId: string,
  options: ListCommentsOptions = {},
): Promise<FeedComment[]> {
  const query: Record<string, string> = {
    limit: String(options.limit ?? COMMENTS_PAGE_SIZE),
  };
  if (options.before) query.before = options.before;

  return orgRequest<FeedComment[]>(
    client,
    "GET",
    `/org/posts/${postId}/comments`,
    { query },
  );
}

/**
 * INSIDO (09e-comentarios-web): comment on a post
 * @param client Client
 * @param postId Post id
 * @param body Plain-text body (1..2000 characters)
 */
export async function createComment(
  client: Client,
  postId: string,
  body: string,
): Promise<FeedComment> {
  return orgRequest<FeedComment>(
    client,
    "POST",
    `/org/posts/${postId}/comments`,
    { body: { body } },
  );
}

/**
 * INSIDO (09e-comentarios-web): delete a comment (author or ManageMessages)
 * @param client Client
 * @param commentId Comment id
 */
export async function deleteComment(
  client: Client,
  commentId: string,
): Promise<void> {
  await orgRequest<null>(client, "DELETE", `/org/comments/${commentId}`);
}
